import { useEffect, useState, useCallback } from "react";
import { useHub } from "../../context/HubContext";
import { useAuth } from "../../context/AuthContext";
import {
  getHubWallet,
  getWalletTransactions,
  getEligiblePayoutEvents,
} from "../../services/walletService";
import { getHubById } from "../../services/hubService";
import PageHeader from "../../components/common/PageHeader";
import WalletSummaryCards from "../../components/ingroove/wallet/WalletSummaryCards";
import TransactionsList from "../../components/ingroove/wallet/TransactionsList";
import RequestPayoutModal from "../../components/ingroove/wallet/RequestPayoutModal";
import BankingDetailsSection from "../../components/ingroove/wallet/BankingDetailsSection";
import { Wallet as WalletIcon, ArrowUpRight, Search, X } from "lucide-react";
import { formatCurrency } from "../../utils/formatters";

const Wallet = () => {
  const { hub } = useHub();
  const { user } = useAuth();

  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [eligibleEvents, setEligibleEvents] = useState([]);
  const [hubDetail, setHubDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(true);
  const [showPayout, setShowPayout] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const fetchWallet = useCallback(async () => {
    if (!hub?.id) return;
    setLoading(true);
    try {
      const w = await getHubWallet(hub.id);
      setWallet(w);
    } finally {
      setLoading(false);
    }
  }, [hub?.id]);

  const fetchTransactions = useCallback(async () => {
    if (!wallet?.id) return;
    setTxLoading(true);
    try {
      const txs = await getWalletTransactions(wallet.id);
      setTransactions(txs);
    } finally {
      setTxLoading(false);
    }
  }, [wallet?.id]);

  const fetchEligible = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await getEligiblePayoutEvents(user.id);
      setEligibleEvents(res);
    } catch {
      // fail silently
    }
  }, [user?.id]);

  const fetchHubDetail = useCallback(async () => {
    if (!hub?.id) return;
    try {
      const res = await getHubById(hub.id);
      setHubDetail(res);
    } catch {
      // fail silently
    }
  }, [hub?.id]);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);
  useEffect(() => {
    fetchEligible();
  }, [fetchEligible]);
  useEffect(() => {
    fetchHubDetail();
  }, [fetchHubDetail]);

  const filteredTransactions = transactions.filter((tx) => {
    const matchesSearch =
      !search ||
      tx.name?.toLowerCase().includes(search.toLowerCase()) ||
      tx.reference?.toLowerCase().includes(search.toLowerCase());

    const isCredit =
      tx.transactionType === 0 || tx.transactionType === "Credit";
    const matchesType =
      typeFilter === "" ? true : typeFilter === "Credit" ? isCredit : !isCredit;

    return matchesSearch && matchesType;
  });

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-24 bg-bg-surface border border-border rounded-2xl"
            />
          ))}
        </div>
        <div className="h-64 bg-bg-surface border border-border rounded-2xl" />
      </div>
    );

  if (!wallet)
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <WalletIcon size={32} className="text-border-hover mb-3" />
        <p className="text-sm text-text-muted">Wallet not found.</p>
      </div>
    );

  const walletWithTx = { ...wallet, transactions };

  const pendingPayouts = transactions.filter(
    (tx) =>
      (tx.transactionType === 1 || tx.transactionType === "Debit") &&
      (tx.transactionDescription === 2 ||
        tx.transactionDescription === "Payout"),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wallet"
        subtitle={`${hub?.name} — hub wallet`}
        action={
          eligibleEvents.length > 0 && (
            <button
              onClick={() => setShowPayout(true)}
              className="flex items-center gap-2 px-4 py-2 ingroove-gradient-bg text-white text-xs font-semibold rounded-xl hover:opacity-90 transition shadow-glow"
            >
              <ArrowUpRight size={14} />
              Request Payout
            </button>
          )
        }
      />

      {/* Balance highlight */}
      <div className="bg-bg-surface border border-border rounded-2xl p-6 flex items-center justify-between">
        <div>
          <p className="text-xs text-text-muted uppercase tracking-wide mb-1">
            Current Balance
          </p>
          <p className="text-3xl font-bold ingroove-gradient-text">
            {formatCurrency(wallet.balance)}
          </p>
          <p className="text-xs text-text-muted mt-1">{hub?.name} Hub Wallet</p>
        </div>
        <div className="w-14 h-14 rounded-2xl ingroove-gradient-bg flex items-center justify-center shadow-glow">
          <WalletIcon size={24} className="text-white" />
        </div>
      </div>

      {/* Summary cards */}
      <WalletSummaryCards
        wallet={walletWithTx}
        pendingPayouts={pendingPayouts}
      />

      {/* Transactions + Banking Details side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Transactions — takes 2/3 */}
        <div className="lg:col-span-2 bg-bg-surface border border-border rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary">
              Transaction History
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 pr-4 py-1.5 text-xs bg-bg-base border border-border rounded-xl text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition w-44"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-hint hover:text-text-muted"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>

              {/* Type filter */}
              <div className="flex gap-1 bg-bg-base border border-border rounded-xl p-1">
                {["", "Credit", "Debit"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setTypeFilter(f)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                      typeFilter === f
                        ? "ingroove-gradient-bg text-white shadow-glow"
                        : "text-text-muted hover:text-text-secondary"
                    }`}
                  >
                    {f === "" ? "All" : f}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <TransactionsList
            transactions={filteredTransactions}
            loading={txLoading}
          />
        </div>

        {/* Banking details — takes 1/3, sticky */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          {hubDetail && (
            <BankingDetailsSection hub={hubDetail} onUpdated={fetchHubDetail} />
          )}
        </div>
      </div>

      {/* Request payout modal */}
      {showPayout && (
        <RequestPayoutModal
          onClose={() => setShowPayout(false)}
          onSuccess={() => {
            fetchWallet();
            fetchTransactions();
            fetchEligible();
          }}
        />
      )}
    </div>
  );
};

export default Wallet;
